/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Checkbox, Dialog, Table, TableTypes } from "tvs-unified-lib";
import { ColumnDef } from "@tanstack/react-table";
import { dataSetp3 } from "../constant";
import { ChangeEvent, Fragment, useEffect, useMemo, useState } from "react";
import { FlexButton } from "../styles";
import { uploadApi } from "@/services";
import { useSnackbar } from "notistack";

interface Props {
  setStep: (step: number) => void;
  setFileId: (fileId: number) => void;
  file: any;
  handleCancel: () => void;
  selected: string[];
  setSelected: (selected: string[]) => void;
}

export const Step3 = ({
  file,
  setStep,
  handleCancel,
  setFileId,
  selected,
  setSelected,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSelectionLimitPopup, setOpenSelectionLimitPopup] =
    useState<boolean>(false);
  const [data, setData] = useState<any>(null);

  const mandatoryChecked = (name: string) => {
    return selected.find((t) => t === name) ? "Yes" : "No";
  };

  const userFieldMapping = useMemo(() => {
    if (!data || !data.excelHeaders || !data.headerMapping) {
      return undefined;
    }

    const normalizeHeaderForComparison = (
      str: string | null | undefined
    ): string => {
      if (!str) return "";
      return str.replace(/ /g, "").replace(/\./g, "").toLowerCase();
    };

    const processedExcelHeaders = data.excelHeaders
      .filter(
        (excelHeaderName: string) =>
          !dataSetp3.some(
            (d) =>
              normalizeHeaderForComparison(d.name) ===
              normalizeHeaderForComparison(excelHeaderName)
          )
      )
      .map((excelHeaderName: string) => {
        let mappedType = "";
        const normalizedCurrentExcelHeader =
          normalizeHeaderForComparison(excelHeaderName);

        for (const keyInMapping in data.headerMapping) {
          if (
            Object.prototype.hasOwnProperty.call(
              data.headerMapping,
              keyInMapping
            )
          ) {
            if (
              normalizeHeaderForComparison(keyInMapping) ===
              normalizedCurrentExcelHeader
            ) {
              mappedType = data.headerMapping[keyInMapping];
              break;
            }
          }
        }

        return {
          name: excelHeaderName,
          type: mappedType || "",
          campaignFilter: true,
          mandatory: null,
        };
      });

    return dataSetp3.concat(processedExcelHeaders);
  }, [data]);

  const fetchData = async () => {
    const params = {
      file_uploaded_url: file?.fileUploadedUrl,
      file_name: file?.fileName,
      type: "campaigns",
    };
    setIsLoading(true);
    await uploadApi
      .create(params)
      .then(({ data }) => {
        if (!data) return;
        setData(data);
        setFileId(data?.id);
      })
      .catch((err) => {
        enqueueSnackbar(err, {
          variant: "error",
        });
        setStep(2);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!file?.fileUploadedUrl) return;
    fetchData();
  }, [file?.fileUploadedUrl]);

  const templateColumns: ColumnDef<any>[] = [
    {
      header: () => "Data Name",
      enableSorting: false,
      accessorKey: "name",
      size: 50,
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue();
        return value;
      },
    },
    {
      header: () => "Data Type",
      enableSorting: false,
      accessorKey: "type",
      size: 50,
      cell: ({ getValue }: TableTypes.CellType<any>) => {
        const value = getValue();
        return value ? value.replace(/\d/g, "") : "-";
      },
    },
    {
      header: () => "Campaign Filter",
      enableSorting: false,
      accessorKey: "campaignFilter",
      size: 50,
      cell: ({ getValue, row }: TableTypes.CellType<any>) => {
        const value = getValue();
        return value ? (
          <Checkbox
            formLabel=""
            name={`check-${row.original.name}`}
            value={selected}
            checkboxValues={[{ label: "", value: row.original.name }]}
            onInputChange={(e: ChangeEvent, currentItemValue: string) => {
              const isChecked = (e.target as HTMLInputElement).checked;

              if (!userFieldMapping) return;

              const currentItem = userFieldMapping.find(
                (item) => item.name === currentItemValue
              );
              if (!currentItem) return;

              const currentItemType = currentItem.type as string;
              let partnerItemName: string | undefined = undefined;

              if (currentItemType?.startsWith("ListCode")) {
                const suffix = currentItemType.substring("ListCode".length);
                const descPartnerType = `ListDesc${suffix}`;
                const partner = userFieldMapping.find(
                  (p) => p.type === descPartnerType
                );
                if (partner) partnerItemName = partner.name;
              } else if (currentItemType?.startsWith("ListDesc")) {
                const suffix = currentItemType.substring("ListDesc".length);
                const codePartnerType = `ListCode${suffix}`;
                const partner = userFieldMapping.find(
                  (p) => p.type === codePartnerType
                );
                if (partner) partnerItemName = partner.name;
              }

              const newSelected = new Set(selected);

              if (isChecked) {
                newSelected.add(currentItemValue);
                if (partnerItemName) {
                  newSelected.add(partnerItemName);
                }
              } else {
                newSelected.delete(currentItemValue);
                if (partnerItemName) {
                  newSelected.delete(partnerItemName);
                }
              }
              if (Array.from(newSelected).length > 9) {
                setOpenSelectionLimitPopup(true);
              } else {
                setSelected(Array.from(newSelected));
              }
            }}
          />
        ) : (
          "-"
        );
      },
    },
    {
      header: () => "Mandatory",
      enableSorting: false,
      accessorKey: "mandatory",
      size: 50,
      cell: ({ getValue, row }: TableTypes.CellType<any>) => {
        const value = getValue();
        return value === null ? mandatoryChecked(row.original.name) : value;
      },
    },
  ];

  return (
    <Fragment>
      <Table
        columns={templateColumns}
        data={userFieldMapping || dataSetp3}
        isLoading={isLoading}
        pagination={{
          pageIndex: 0,
          pageSize: 5,
        }}
        setSorting={() => {}}
        sorting={[]}
        totalRecords={5}
        enablePagination={false}
        manualSorting={false}
      />
      <FlexButton>
        <Button label="Cancel" variant="outline" onClick={handleCancel} />
        <FlexButton>
          <Button label="Previous" onClick={() => setStep(2)} />
          <Button
            label="Next"
            onClick={() => {
              setStep(4);
            }}
          />
        </FlexButton>
      </FlexButton>
      {openSelectionLimitPopup && (
        <Dialog
          title="Selection Limit"
          size="sm"
          onBackdropClick={() => setOpenSelectionLimitPopup(false)}
          footerButtons={
            <Button
              label="Ok"
              onClick={() => setOpenSelectionLimitPopup(false)}
            />
          }
        >
          You have reached the limit of 9 selections.
        </Dialog>
      )}
    </Fragment>
  );
};

export default Step3;
